import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { message } from 'antd';
import { v4 as uuidv4 } from 'uuid';
import type {
  OrderItem,
  OrderType,
  FulfillmentMode,
  PaymentMethod,
  CommissionStaff,
  PosCheckoutAttempt,
} from '../types';

export interface PosTabState {
  id: string;
  name: string;
  orderItems: OrderItem[];
  orderType: OrderType;
  fulfillmentMode: FulfillmentMode;
  paymentMethod: PaymentMethod;
  selectedSessionId?: string;
  commissionStaff: CommissionStaff[];
  customerForm: Record<string, unknown>;
  /** Tiền khách đưa (tab cash hoặc split) — display-only FE. */
  cashReceived?: number;
  /** Account đã chọn (bank/ewallet/card) — display-only FE. */
  paymentAccountId?: string;
  /** Số tiền trả qua phương thức phi tiền mặt (split) — display-only FE. */
  nonCashAmount?: number;
  /** Persisted identity/snapshot for a create-order attempt awaiting reconciliation. */
  checkoutAttempt?: PosCheckoutAttempt;
}

export interface PosTabsState {
  tabs: PosTabState[];
  activeId: string;
}

export type PosTabsAction =
  | { type: 'ADD_TAB' }
  /**
   * `replacementId` là id của tab trắng sẽ thay thế khi đây là tab CUỐI CÙNG.
   * Caller sinh sẵn (thay vì reducer tự `uuidv4()`) để `CLOSE_TAB` thuần: hook
   * chạy reducer thêm một lần ngoài `dispatch` để ghi sessionStorage đồng bộ,
   * và hai lần chạy đó phải ra CÙNG một state — nếu không, tab trong RAM và
   * tab dưới storage mang hai id khác nhau.
   */
  | { type: 'CLOSE_TAB'; id: string; replacementId: string }
  | { type: 'SWITCH_TAB'; id: string }
  | { type: 'PATCH_ACTIVE'; patch: Partial<Omit<PosTabState, 'id' | 'name'>> }
  | { type: 'PATCH_TAB'; id: string; patch: Partial<Omit<PosTabState, 'id' | 'name'>> }
  | { type: 'CLEAR_ALL' }
  | { type: 'HYDRATE'; state: PosTabsState };

export const MAX_TABS = 10;
export const STORAGE_KEY = 'pos-tabs-v1';

export function createEmptyTab(name: string, id: string = uuidv4()): PosTabState {
  return {
    id,
    name,
    orderItems: [],
    orderType: 'POS',
    fulfillmentMode: 'INSTANT',
    paymentMethod: 'cash',
    selectedSessionId: undefined,
    commissionStaff: [],
    customerForm: {},
    cashReceived: undefined,
    paymentAccountId: undefined,
    nonCashAmount: undefined,
  };
}

export function getInitialState(): PosTabsState {
  const first = createEmptyTab('Đơn hàng 1');
  return { tabs: [first], activeId: first.id };
}

function nextTabName(tabs: PosTabState[]): string {
  let maxIdx = 0;
  for (const t of tabs) {
    const m = t.name.match(/^Đơn hàng (\d+)$/);
    if (m) {
      const n = parseInt(m[1], 10);
      if (n > maxIdx) maxIdx = n;
    }
  }
  return `Đơn hàng ${maxIdx + 1}`;
}

function isValidTab(t: unknown): t is PosTabState {
  if (typeof t !== 'object' || t === null) return false;
  const obj = t as Record<string, unknown>;
  return (
    typeof obj.id === 'string' &&
    typeof obj.name === 'string' &&
    Array.isArray(obj.orderItems) &&
    typeof obj.orderType === 'string' &&
    typeof obj.fulfillmentMode === 'string' &&
    typeof obj.paymentMethod === 'string' &&
    Array.isArray(obj.commissionStaff) &&
    typeof obj.customerForm === 'object' && obj.customerForm !== null
  );
}

function hydrateFromStorage(): PosTabsState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const obj = parsed as Record<string, unknown>;
    if (!Array.isArray(obj.tabs) || obj.tabs.length === 0) return null;
    const tabs = obj.tabs.filter(isValidTab) as PosTabState[];
    if (tabs.length === 0) return null;
    const activeIdRaw = typeof obj.activeId === 'string' ? obj.activeId : null;
    const activeId = activeIdRaw && tabs.some((t) => t.id === activeIdRaw) ? activeIdRaw : tabs[0].id;
    return { tabs, activeId };
  } catch {
    return null;
  }
}

export interface UsePosTabsReturn {
  state: PosTabsState;
  active: PosTabState;
  addTab: () => void;
  closeTab: (id: string) => void;
  switchTab: (id: string) => void;
  patchActive: (patch: Partial<Omit<PosTabState, 'id' | 'name'>>) => void;
  /** Patch một tab CỤ THỂ theo id — dùng khi patch được tính từ snapshot của tab đó. */
  patchTab: (id: string, patch: Partial<Omit<PosTabState, 'id' | 'name'>>) => void;
  clearAll: () => void;
}

export function reducer(state: PosTabsState, action: PosTabsAction): PosTabsState {
  switch (action.type) {
    case 'ADD_TAB': {
      // FOLLOW-UP (review TCR-1096 vòng 2, finding #5 — CỐ Ý chưa làm ở vòng
      // này): `createEmptyTab` gọi `uuidv4()` NGAY TRONG reducer, nên reducer
      // không thuần — dưới StrictMode nó chạy hai lần và sinh hai id khác nhau,
      // và người gọi `addTab()` không có cách nào biết id tab vừa mở để ghi
      // tiếp vào đó (luồng "Sao chép đơn hàng" hiện phải dựa vào việc `ADD_TAB`
      // tự set `activeId`). Cách sửa: `ADD_TAB` nhận `id` do caller sinh sẵn.
      // Đó là refactor xuyên qua mọi nơi gọi `addTab`, tách riêng khỏi bản vá
      // mất-hàng này để không trộn hai loại rủi ro trong một commit.
      if (state.tabs.length >= MAX_TABS) return state;
      const newTab = createEmptyTab(nextTabName(state.tabs));
      return { tabs: [...state.tabs, newTab], activeId: newTab.id };
    }
    case 'CLOSE_TAB': {
      const idx = state.tabs.findIndex((t) => t.id === action.id);
      if (idx === -1) return state;
      const nextTabs = state.tabs.filter((t) => t.id !== action.id);
      if (nextTabs.length === 0) {
        const empty = createEmptyTab('Đơn hàng 1', action.replacementId);
        return { tabs: [empty], activeId: empty.id };
      }
      let nextActive = state.activeId;
      if (action.id === state.activeId) {
        const leftIdx = Math.max(0, idx - 1);
        nextActive = nextTabs[leftIdx].id;
      }
      return { tabs: nextTabs, activeId: nextActive };
    }
    case 'SWITCH_TAB': {
      if (!state.tabs.some((t) => t.id === action.id)) return state;
      if (state.activeId === action.id) return state;
      return { ...state, activeId: action.id };
    }
    case 'PATCH_ACTIVE': {
      return {
        ...state,
        tabs: state.tabs.map((t) => (t.id === state.activeId ? { ...t, ...action.patch } : t)),
      };
    }
    case 'PATCH_TAB': {
      // Ghi vào ĐÚNG tab đã được đọc để dựng patch, không phải "tab nào đang
      // active lúc dispatch". Xem `setOrderItems` ở POSPage: patch giỏ hàng được
      // TÍNH SẴN từ snapshot của một tab cụ thể; nếu tab active đổi giữa lúc đọc
      // và lúc reducer chạy (`ADD_TAB` của "Sao chép đơn hàng", thu ngân đổi tab
      // khi một `setOrderItems` async còn bay), `PATCH_ACTIVE` sẽ đắp snapshot
      // của tab CŨ lên giỏ của tab MỚI — mất hàng, im lặng.
      if (!state.tabs.some((t) => t.id === action.id)) return state; // tab đã đóng ⇒ bỏ ghi
      return {
        ...state,
        tabs: state.tabs.map((t) => (t.id === action.id ? { ...t, ...action.patch } : t)),
      };
    }
    case 'CLEAR_ALL':
      return getInitialState();
    case 'HYDRATE':
      return action.state;
    default:
      return state;
  }
}

function persist(state: PosTabsState): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage full or disabled — silently ignore
  }
}

/**
 * `state` có còn chứa tab ĐÃ ĐÓNG không — tức nó là ảnh chụp cũ hơn lần ghi
 * đồng bộ của `closeTab`, và ghi nó xuống storage sẽ làm sống lại đơn đã xong.
 *
 * Vì sao cần: khi POSPage đóng tab RỒI ĐIỀU HƯỚNG ĐI trong cùng một batch (ghi
 * hoá đơn thành công), React vứt luôn update `CLOSE_TAB` — reducer không bao
 * giờ chạy — nhưng effect persist vẫn còn kịp chạy thêm một nhịp với state CŨ.
 * Nhịp đó ghi đè bản đã đóng tab, và lần sau mở POS `hydrateFromStorage()`
 * dựng lại nguyên đơn vừa thanh toán (TCR-1223).
 */
export function hasClosedTab(state: PosTabsState, closedIds: ReadonlySet<string>): boolean {
  if (closedIds.size === 0) return false;
  return state.tabs.some((t) => closedIds.has(t.id));
}

export function usePosTabs(): UsePosTabsReturn {
  const [state, dispatch] = useReducer(
    reducer,
    undefined,
    () => hydrateFromStorage() ?? getInitialState(),
  );

  // State đã commit gần nhất, đọc được NGAY trong event handler / effect —
  // `closeTab` cần nó để tự tính state kế tiếp mà không phải đợi render sau.
  const stateRef = useRef(state);
  stateRef.current = state;

  /** Tab đã đóng trong phiên này — xem `hasClosedTab`. */
  const closedIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (hasClosedTab(state, closedIdsRef.current)) return;
    persist(state);
  }, [state]);

  const active = useMemo(
    () => state.tabs.find((t) => t.id === state.activeId) ?? state.tabs[0],
    [state.tabs, state.activeId],
  );

  const addTab = useCallback(() => {
    if (state.tabs.length >= MAX_TABS) {
      void message.warning(`Tối đa ${MAX_TABS} đơn cùng lúc`);
      return;
    }
    dispatch({ type: 'ADD_TAB' });
  }, [state.tabs.length]);

  const closeTab = useCallback((id: string) => {
    const action: PosTabsAction = { type: 'CLOSE_TAB', id, replacementId: uuidv4() };
    dispatch(action);
    // TCR-1223 — KHÔNG chờ `useEffect` persist ở trên.
    //
    // Lối đóng tab quan trọng nhất (ghi hoá đơn xong) gọi `closeTab` rồi
    // `navigate('/orders...')` trong CÙNG một batch. React bỏ luôn update
    // `CLOSE_TAB` vì POSPage unmount ngay trong commit đó — reducer không hề
    // chạy — nên state trong RAM vẫn là tab cũ. Vì thế bản ghi "đã đóng tab"
    // phải được tính và ghi thẳng ở đây, và mọi lần ghi sau từ state cũ phải
    // bị chặn (`closedIdsRef` + `hasClosedTab`); không có cả hai thì lần sau
    // mở POS `hydrateFromStorage()` dựng lại nguyên đơn vừa thanh toán.
    //
    // `CLOSE_TAB` thuần (xem `replacementId`) nên chạy reducer lần thứ hai ở
    // đây cho ra đúng state mà `dispatch` sẽ commit — không lệch id.
    closedIdsRef.current.add(id);
    persist(reducer(stateRef.current, action));
  }, []);
  const switchTab = useCallback((id: string) => dispatch({ type: 'SWITCH_TAB', id }), []);
  const patchActive = useCallback(
    (patch: Partial<Omit<PosTabState, 'id' | 'name'>>) =>
      dispatch({ type: 'PATCH_ACTIVE', patch }),
    [],
  );
  const patchTab = useCallback(
    (id: string, patch: Partial<Omit<PosTabState, 'id' | 'name'>>) =>
      dispatch({ type: 'PATCH_TAB', id, patch }),
    [],
  );
  const clearAll = useCallback(() => dispatch({ type: 'CLEAR_ALL' }), []);

  return { state, active, addTab, closeTab, switchTab, patchActive, patchTab, clearAll };
}
